import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();
    const { videoSources, videoElement } = this.refs;
    if (!videoElement) return;
    this._videoAbort?.abort();
    this._videoObserver?.disconnect();
    this._videoAbort = new AbortController();
    const signal = this._videoAbort.signal;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._videoInView = false;
    this._backgroundVideo = videoElement;
    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.playsInline = true;
    let sourcesChanged = false;
    for (const source of videoSources) {
      const url = source.dataset.videoSource;
      if (url && source.getAttribute('src') !== url) {
        source.setAttribute('src', url);
        sourcesChanged = true;
      }
    }
    this._syncBackgroundVideo = () => {
      const canPlay = this._videoInView && !document.hidden && !reduced.matches;
      if (!canPlay) {
        if (!videoElement.paused) videoElement.pause();
        return;
      }
      videoElement.muted = true;
      if (videoElement.paused) {
        const attempt = videoElement.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      }
    };
    if (sourcesChanged) videoElement.load();
    if ('IntersectionObserver' in window) {
      this._videoObserver = new IntersectionObserver((entries) => {
        this._videoInView = Boolean(entries[0]?.isIntersecting);
        this._syncBackgroundVideo();
      }, { threshold: 0.01 });
      this._videoObserver.observe(this);
    } else {
      this._videoInView = true;
      this._syncBackgroundVideo();
    }
    document.addEventListener('visibilitychange', this._syncBackgroundVideo, { signal });
    window.addEventListener('pageshow', this._syncBackgroundVideo, { signal });
    if (reduced.addEventListener) {
      reduced.addEventListener('change', this._syncBackgroundVideo, { signal });
    } else {
      this._legacyMotionQuery = reduced;
      this._legacyMotionHandler = this._syncBackgroundVideo;
      reduced.addListener?.(this._legacyMotionHandler);
    }
  }
  disconnectedCallback() {
    this._videoObserver?.disconnect();
    this._videoAbort?.abort();
    this._legacyMotionQuery?.removeListener?.(this._legacyMotionHandler);
    this._backgroundVideo?.pause();
    super.disconnectedCallback();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
